import React, { useState, useEffect } from 'react';
import { useRequestData, useLabels } from 'wsm-common-data';
import PropTypes from 'prop-types';
import Rheostat from 'rheostat';
import 'rheostat/initialize';
import { formatNumber } from 'ddc-intl';
import { hasUrlParam } from 'wsm-srp-utilities';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';

import {
	rangeContainsInventory,
	getLowerBound,
	getUpperBound,
	fixRangeForSubmit,
	getBucketMap,
	isNoPriceActive
} from '../../utilities/facet-user-range';
import RangeForm from './RangeForm';
import NoPriceButton from './NoPriceButton';
import SliderBullets from './SliderBullets';
import { noPrice } from '../../global-constants';

const HISTOGRAM_HEIGHT = 100;

const FacetUserRange = ({
	facetValues,
	selectedValues,
	facetId,
	noPriceObj,
	isMultiselect
}) => {
	const {
		min,
		max,
		bucketSize,
		values,
		selectedMin = null,
		selectedMax = null,
		isCurrency = false
	} = facetValues;
	const { widgetName, windowId, locale } = useRequestData();
	const labels = useLabels();

	// Setting a variable to offset the range slider when bucketSize equals 1.
	// This `offset` variable is incorporated within the max/min state values
	// to allow the slider and histogram to display properly for "year" facets.
	const offset = bucketSize === 1 ? 1 : 0;

	// Lowest and highest buckets
	const bottomLimit = getLowerBound(min, bucketSize, facetId, isMultiselect);
	const topLimit = getUpperBound(max, bucketSize);
	const topLimitOffset = getUpperBound(max + offset, bucketSize);

	// Values of min and max forms, slider and histogram
	const currentMin = selectedMin || bottomLimit;
	const currentMax = selectedMax || topLimit;
	const currentMaxOffset = selectedMax
		? selectedMax + offset
		: null || topLimitOffset;
	const [noPriceButton, setNoPriceButton] = useState(noPriceObj);
	const [minInput, setMinInput] = useState(currentMin);
	const [maxInput, setMaxInput] = useState(currentMax);
	const [maxInputOffset, setMaxInputOffset] = useState();
	useEffect(() => {
		setMinInput(currentMin);
		setMaxInput(currentMax);
		setMaxInputOffset(currentMaxOffset);
		if (noPriceObj) {
			setNoPriceButton(noPriceObj);
		}
	}, [currentMin, currentMax, currentMaxOffset, noPriceObj]);

	const showHistogram = window.navigator.userAgent.indexOf('Trident/') < 0; // Disable for IE11
	const isDisabled = !hasUrlParam(window.location.href, facetId);

	// Map of buckets containing values with bucket values as keys
	const bucketMap = getBucketMap(values);
	const histogramScale =
		HISTOGRAM_HEIGHT /
		Math.max(
			...values.map(v => {
				return v.count;
			})
		);

	const pitPoints = [];
	const histogram = [];

	for (let point = bottomLimit; point < topLimitOffset; point += bucketSize) {
		pitPoints.push(point);
		const height = bucketMap.has(point)
			? Math.round(histogramScale * bucketMap.get(point).count) + 10
			: 0;

		// Create Histogram
		const activeClass =
			currentMin <= point && point < currentMaxOffset ? 'active' : '';
		histogram.push(
			<div
				key={`${facetId}-${point}`}
				id={`${facetId}-${point}`}
				className={setClassNames(['histogram-bar', activeClass])}
				style={{
					height
				}}
			/>
		);

		if (point === 1) {
			point = 0;
		}
	}
	pitPoints.push(topLimitOffset);

	const handleSubmit = (newMin, newMax, isOffset = false) => {
		const [bottom, top] = fixRangeForSubmit(
			newMin,
			isOffset ? newMax - offset : newMax,
			bottomLimit,
			topLimit,
			bucketSize,
			newMin !== currentMin
		);
		const containsInventory = rangeContainsInventory(
			bottom,
			top,
			bucketSize,
			bucketMap,
			facetId
		);
		const isNewRange = bottom !== currentMin || top !== currentMax;

		if (isNewRange && containsInventory) {
			setMinInput(bottom);
			setMaxInput(top);
			setMaxInputOffset(top + offset);

			const updatedSelections = [];
			const sliderValue = `${bottom}-${top}`;

			updatedSelections.push(sliderValue);

			if (
				selectedValues &&
				selectedValues.find(value => value.value === noPrice)
			) {
				updatedSelections.push(noPrice);
			}

			window.DDC.InvData.setFilterSelections({
				filterName: facetId,
				filterValue: updatedSelections[0],
				filterValues: updatedSelections,
				isChip: false,
				isSelected: false,
				isSlider: true,
				element: document.getElementById(facetId)
			});
		} else if (!containsInventory) {
			setMinInput(currentMin);
			setMaxInput(currentMax);
			setMaxInputOffset(currentMaxOffset);
		}
	};

	const updateInputValues = (minVal, maxVal) => {
		const bottomBucket = getLowerBound(
			minVal,
			bucketSize,
			facetId,
			isMultiselect
		);
		const topBucket = getUpperBound(
			maxVal - offset > minVal ? maxVal - offset : minVal,
			bucketSize
		);
		const topBucketOffset = getUpperBound(maxVal, bucketSize);

		setMinInput(bottomBucket);
		setMaxInput(topBucket);
		setMaxInputOffset(topBucketOffset);
	};

	const getVehicleCount = () => {
		const vehicleCount = window.DDC.InvData.getFacets().totalCount;
		const matchesLabel =
			vehicleCount !== 1 ? labels.get('MATCHES') : labels.get('MATCH');
		return `${formatNumber(vehicleCount, locale)} ${matchesLabel}`;
	};

	const minValue = Math.max(minInput, bottomLimit);
	const maxValue = Math.min(maxInput, topLimit);
	const maxValueOffset = Math.min(maxInputOffset, topLimitOffset);

	return (
		<div className="facet-list-range-slider">
			<RangeForm
				id={facetId}
				minValue={bottomLimit}
				maxValue={topLimit}
				minInput={minValue}
				maxInput={maxValue}
				minLabel={labels.get('MIN')}
				maxLabel={labels.get('MAX')}
				handleSubmit={handleSubmit}
				isCurrency={isCurrency}
			/>
			<div className="vehicle-count">{getVehicleCount()}</div>
			{showHistogram && (
				<div className="range-slider">
					<div className="histogram">{histogram}</div>
					<Rheostat
						snap
						snapPoints={pitPoints}
						pitComponent={e => (
							<SliderBullets
								{...e}
								bottomLimit={bottomLimit}
								topLimit={topLimitOffset}
							/>
						)}
						min={bottomLimit}
						max={topLimitOffset}
						values={[minValue, maxValueOffset]}
						pitPoints={pitPoints}
						onChange={e => {
							handleSubmit(
								e.values[0],
								e.values[1],
								offset !== 0
							);
							trackEvent(widgetName, windowId, {
								action: 'applied',
								element: `${facetId} selection`,
								elementCTA: 'Range Slider',
								result: 'Results updated with facet added'
							});
						}}
						onValuesUpdated={e =>
							updateInputValues(e.values[0], e.values[1])
						}
					/>
				</div>
			)}
			{noPriceButton && noPriceButton.count && (
				<NoPriceButton
					id={facetId}
					noPriceCount={noPriceButton.count}
					noPriceLabel={noPriceButton.label}
					selectedValues={selectedValues}
					isSelected={isNoPriceActive(selectedValues)}
					isDisabled={isDisabled}
				/>
			)}
		</div>
	);
};

FacetUserRange.propTypes = {
	facetValues: PropTypes.shape({
		min: PropTypes.number.isRequired,
		max: PropTypes.number.isRequired,
		bucketSize: PropTypes.number.isRequired,
		values: PropTypes.arrayOf(
			PropTypes.shape({
				count: PropTypes.number.isRequired,
				label: PropTypes.string.isRequired,
				type: PropTypes.string.isRequired,
				value: PropTypes.string.isRequired
			})
		).isRequired,
		selectedMin: PropTypes.number,
		selectedMax: PropTypes.number,
		isCurrency: PropTypes.bool
	}).isRequired,
	noPriceObj: PropTypes.shape({
		count: PropTypes.number,
		label: PropTypes.string
	}),
	facetId: PropTypes.string.isRequired,
	isMultiselect: PropTypes.bool.isRequired,
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

FacetUserRange.defaultProps = {
	noPriceObj: null
};

export default FacetUserRange;
